import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { cn } from "@shared/lib/utils";
import {
  useAddSlipLotEntry,
  usePaymentMethod,
  useOutstandingBills,
  useUpdateSlipLotEntry,
} from "../../../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { normalizeDate } from "@shared/utils/dateUtils";

// Helper to consistently read the consumer's account/registration number
// off of a consumer record, regardless of which shape it arrived in
// (API search result uses `consumer_no`; some callers/initialData use
// `consumerAccount` / `consumer_account` / `accountNo`).
const getConsumerAccountNo = (consumer: any): string =>
  consumer?.consumerNo || "";

const AsyncConsumerSelect = ({
  value,
  onChange,
  onSelect,
  disabled,
  placeholder,
  initialDisplay,
}: any) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const remoteUtilityId = useRemoteUtilityId();

  const { data: outstandingBillsResponse, isFetching } = useOutstandingBills({
    searchData: debouncedSearch ? debouncedSearch.split(" - ")[0] : "",
    remoteUtilityId: Number(remoteUtilityId),
  });

  const consumers = React.useMemo(() => {
    const rawBills = outstandingBillsResponse?.result || [];
    return rawBills.map((b: any) => ({
      id: b.consumerId,
      name: b.fullName || `${b.firstName || ""} ${b.lastName || ""}`.trim(),
      consumerNo: b.remoteConsumerNumber,
      invoices: [
        {
          id: b.billNumber,
        },
      ],
    }));
  }, [outstandingBillsResponse]);

  useEffect(() => {
    if (value) {
      if (!searchTerm || String(value) === searchTerm) {
        setSearchTerm(initialDisplay || String(value));
      }
    } else {
      setSearchTerm("");
    }
  }, [value, initialDisplay]);

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "flex w-full items-center justify-between h-[42px] px-3 rounded-md font-normal transition-all duration-200 cursor-text border text-sm",
            "border-gray-300 bg-white hover:border-blue-400 focus-within:border-blue-500",
            open ? "border-blue-500 ring-2 ring-blue-500/10" : "",
            disabled
              ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
              : "text-gray-900",
          )}
          onClick={() => !disabled && setOpen(true)}
        >
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (!open) setOpen(true);
            }}
            placeholder={placeholder || "Search consumer by ID or Name..."}
            className={cn(
              "flex-1 min-w-0 bg-transparent outline-none truncate",
              !searchTerm
                ? "placeholder:text-gray-500"
                : "placeholder:text-gray-900",
            )}
            disabled={disabled}
          />
          {isFetching ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
          ) : (
            <ChevronDown
              className={cn(
                "ml-2 h-4 w-4 shrink-0 transition-transform duration-200 opacity-50",
                open ? "rotate-180" : "",
              )}
            />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 border border-gray-100 bg-white shadow-lg rounded-md z-[9999]"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="py-1 max-h-[250px] overflow-y-auto">
          {!debouncedSearch ? (
            <div className="py-4 text-center text-sm text-gray-500">
              Type to search...
            </div>
          ) : isFetching ? (
            <div className="py-4 text-center text-sm text-gray-500 flex justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : consumers?.length === 0 ? (
            <div className="py-4 text-center text-sm text-gray-500">
              No consumers found.
            </div>
          ) : (
            consumers?.map((c: any) => (
              <div
                key={c.id}
                onClick={() => {
                  onChange(String(c.id));
                  onSelect(c);
                  setSearchTerm(
                    `${getConsumerAccountNo(c) || c.id} - ${c.name}`,
                  );
                  setOpen(false);
                }}
                className={cn(
                  "flex flex-col gap-0.5 px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors",
                  value === c.id ? "bg-blue-50" : "",
                )}
              >
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-gray-500">
                  Acc: {getConsumerAccountNo(c) || c.id}
                </span>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface SlipEntryModalProps {
  data: any;
  isOpen: boolean;
  onClose: () => void;
  initialData?: any;
  onSubmit: (data: any, addAnother?: boolean) => void;
}

export const SlipEntryModal: React.FC<SlipEntryModalProps> = ({
  data,
  isOpen,
  onClose,
  initialData,
  onSubmit,
}) => {
  const [warningMessage, setWarningMessage] = useState("");
  const [selectedConsumer, setSelectedConsumer] = useState<any>(null);

  const { id: lotId } = useParams<{ id: string }>();
  const addEntryMutation = useAddSlipLotEntry(lotId || "");
  const updateEntryMutation = useUpdateSlipLotEntry(lotId || "");

  const remoteUtilityId = useRemoteUtilityId();
  const { data: paymentMethods } = usePaymentMethod(Number(remoteUtilityId));

  const fields: FormField[] = [
    {
      name: "accountNo",
      label: "Account No",
      type: "custom" as any,
      required: true,
      customRender: (field: any, form: any) => {
        const accountValue = form?.watch("accountNo");
        let accountHelperText: React.ReactNode = "";
        if (accountValue) {
          if (!selectedConsumer) {
            accountHelperText = (
              <span className="text-red-600 font-medium">
                Consumer not found — entry will be flagged on submit.
              </span>
            );
          }
        }

        return (
          <div className="flex flex-col gap-1">
            <AsyncConsumerSelect
              value={accountValue}
              initialDisplay={
                selectedConsumer
                  ? `${getConsumerAccountNo(selectedConsumer) || accountValue} - ${selectedConsumer.name}`
                  : undefined
              }
              onChange={(v: any) => {
                form.setValue("accountNo", v);
                if (!v) setSelectedConsumer(null);
              }}
              onSelect={(consumer: any) => {
                setSelectedConsumer({
                  ...consumer,
                  invoices: consumer.invoices || [],
                });
              }}
            />
            {accountHelperText && (
              <span className="text-[0.8rem] text-muted-foreground">
                {accountHelperText}
              </span>
            )}
          </div>
        );
      },
      className: "col-span-2",
    },
    {
      name: "invoiceNo",
      label: "Invoice No",
      type: "select",
      disabled: true,
      placeholder: !selectedConsumer
        ? "Consumer unknown"
        : !selectedConsumer.invoices?.length
          ? "No invoices"
          : "Select invoice",
      options:
        selectedConsumer?.invoices.map((i: any) => ({
          label: `${i.id}`,
          value: i.id,
        })) || [],
      required: false,
      className: "col-span-2",
    },
    {
      name: "slipNo",
      label: "Slip No",
      type: "text",
      required: true,
      placeholder: "SLP-xxxxxx",
      helperText:
        "Auto-suggested — override with the number printed on the physical slip.",
      className: "col-span-1 font-mono",
    },
    {
      name: "centre",
      label: "Centre",
      type: "select",
      searchable: true,
      required: true,
      options: [
        {
          label: "DTW - Downtown Payment Centre",
          value: "DTW",
        },
        { label: "WST - Westside Outlet", value: "WST" },

        {
          label: "APT - Airport Terminal Kiosk",
          value: "APT",
        },
        {
          label: "CTH - City Hall Counter",
          value: "CTH",
        },
        {
          label: "MOL - Mall Outlet — Park Ave",
          value: "MOL",
        },
        {
          label: "GRC - Grocery Chain Partner",
          value: "GRC",
        },
      ],
      helperText: "Pre-filled from the lot. Must match the lot's centre.",
      className: "col-span-1",
    },
    {
      name: "mode",
      label: "Mode",
      type: "select",
      required: true,
      placeholder: "— Select mode —",
      options:
        paymentMethods?.result?.map((m: any) => ({
          label: m.name,
          value: m.code,
        })) || [],
      className: "col-span-2",
    },
    // Payment fields group
    {
      name: "payment",
      label: "Payment",
      type: "number",
      required: true,
      placeholder: "0.00",
      prefix: "$",
      min: 0,
      step: 0.01,
      showWhen: (values) => !!values.mode,
      className: "col-span-1",
    },
    {
      name: "paymentDate",
      label: "Payment Date",
      type: "datepicker",
      required: true,
      helperText: "Must be within the last 7 days. Future dates are blocked.",
      showWhen: (values) => !!values.mode,
      className: "col-span-1",
    },
    // Check Details section
    {
      name: "checkLabel",
      label: "",
      type: "custom" as any,
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      customRender: () => (
        <h3 className="font-bold text-gray-900 col-span-2 mt-2 border-b pb-2">
          Check Details
        </h3>
      ),
      className: "col-span-2",
    },
    {
      name: "checkNo",
      label: "Check No",
      type: "text",
      required: true,
      placeholder: "e.g. 234567",
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      className: "col-span-1",
    },
    {
      name: "checkDate",
      label: "Check Date",
      type: "datepicker",
      required: true,
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      className: "col-span-1",
    },
    {
      name: "bankName",
      label: "Bank Name",
      type: "text",
      required: true,
      placeholder: "e.g. Chase, Bank of America",
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      className: "col-span-1",
    },
    {
      name: "branch",
      label: "Branch",
      type: "text",
      placeholder: "Optional branch name",
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      className: "col-span-1",
    },
    {
      name: "micr",
      label: "MICR / Routing No",
      type: "text",
      placeholder: "Optional",
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      className: "col-span-1",
    },
    {
      name: "payeeName",
      label: "Name on Check",
      type: "text",
      placeholder: "If different from consumer name",
      showWhen: (values) =>
        values.mode?.startsWith("Check") || values.mode?.startsWith("Cheque"),
      className: "col-span-1",
    },
    {
      name: "notes",
      label: "Internal Notes",
      type: "textarea",
      placeholder: "Add any specific instructions or remarks...",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: fields,
    defaultValues: initialData || {
      slipNo: `SLP-${Math.floor(100000 + Math.random() * 900000)}`,
      centre: data?.centreCode,
      paymentDate: "",
      mode: "", // start empty
    },
  });

  const accountValue = form.watch("accountNo");

  // Handle resets when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        form.reset({
          accountNo:
            initialData.consumerId ||
            initialData.consumer ||
            initialData.consumerAccount ||
            initialData.consumer_account
              ? String(
                  initialData.consumerId ||
                    initialData.consumer ||
                    initialData.consumerAccount ||
                    initialData.consumer_account,
                )
              : "",
          invoiceNo: initialData.invoiceNumber || "",
          slipNo: initialData.slipNumber || "",
          mode: initialData.mode,
          payment: initialData.amount || "",
          paymentDate: normalizeDate(initialData.paymentDate) || "",
          notes: initialData.notes || "",
          centre: initialData.centreCode || "DTW",
          checkNo: initialData.chequeNumber || "",
          checkDate: normalizeDate(initialData.chequeDate) || "",
          bankName: initialData.bankName || "",
          branch: initialData.branch || "",
          micr: initialData.micrIfsc || "",
          payeeName: initialData.payeeName || "",
        });
        setSelectedConsumer({
          id:
            initialData.consumerId ||
            initialData.consumer ||
            initialData.consumerAccount ||
            initialData.consumer_account,
          name:
            initialData.consumerName && initialData.consumerName !== "undefined"
              ? initialData.consumerName
              : null,

          consumerNo:
            initialData.consumerAccount || initialData.consumer_account,
          invoices: initialData.invoiceNumber
            ? [
                {
                  id: initialData.invoiceNumber,
                  period: "Existing",
                  amount: parseFloat(initialData.amount) || 0,
                },
              ]
            : [],
        });
      } else {
        form.reset({
          slipNo: `SLP-${Math.floor(100000 + Math.random() * 900000)}`,
          centre: data?.centreCode,
          paymentDate: "",
          mode: "",
        });
        setSelectedConsumer(null);
      }
      setWarningMessage("");
    }
  }, [isOpen, initialData, form]);

  // Handle auto-population of payment amount on invoice logic
  useEffect(() => {
    if (selectedConsumer && selectedConsumer.invoices.length > 0) {
      const currentAmount = form.getValues("payment");
      if (!currentAmount) {
        form.setValue("payment", selectedConsumer.invoices[0].amount);
      }
      const currentInvoice = form.getValues("invoiceNo");
      if (!currentInvoice) {
        form.setValue("invoiceNo", selectedConsumer.invoices[0].id);
      }
    }
  }, [accountValue, selectedConsumer, form]);

  const invoiceValue = form.watch("invoiceNo");
  useEffect(() => {
    if (invoiceValue) {
      const inv = selectedConsumer?.invoices.find(
        (i: any) => i.id === invoiceValue,
      );
      if (inv && !form.getValues("payment")) {
        form.setValue("payment", inv.amount);
      }
    }
  }, [invoiceValue, selectedConsumer, form]);

  let accountHelperText: React.ReactNode = "";
  if (accountValue) {
    if (!selectedConsumer) {
      accountHelperText = (
        <span className="text-red-600 font-medium">
          Consumer not found — entry will be flagged on submit.
        </span>
      );
    }
  }

  // Always source payload fields from the form's current values (via
  // form.getValues()) rather than from whatever argument the caller passed
  // in. This keeps the payload-building logic identical whether the entry
  // point is the normal submit handler (DynamicForm's onSubmit callback) or
  // the "Add & Another" button.
  const handleFormSubmit = (_data: any, isAddAnother = false) => {
    const values = form.getValues();

    let warning = "";
    if (!selectedConsumer && accountValue) {
      warning = `Consumer not found. The slip is quarantined and will not post until corrected.`;
      setWarningMessage(warning);
    } else {
      setWarningMessage("");
    }

    const modeObj = paymentMethods?.result?.find(
      (m: any) => m.code === values.mode,
    );
    const modeLabel = modeObj?.name || "";
    const isCheck =
      modeLabel.startsWith("Check") || modeLabel.startsWith("Cheque");

    const consumerAccountNo =
      getConsumerAccountNo(selectedConsumer) || values.accountNo;

    let payload: any = {};
    const actionType = initialData ? "edit" : "add";

    switch (actionType) {
      case "edit":
        // Edit mode: only include changed fields
        if (
          values.accountNo !== (initialData.consumerId || initialData.consumer)
        ) {
          payload.consumer_account = consumerAccountNo;
          payload.consumer_id = values.accountNo;
        }
        if (values.invoiceNo !== initialData.invoiceNumber)
          payload.invoice_number = values.invoiceNo;
        if (values.slipNo !== initialData.slipNumber)
          payload.slip_number = values.slipNo;
        if (values.mode !== initialData.mode) payload.mode = values.mode;
        if (values.payment !== initialData.amount)
          payload.amount = values.payment;
        if (values.paymentDate !== initialData.paymentDate)
          payload.payment_date = values.paymentDate;
        if (values.notes !== initialData.notes)
          payload.notes = values.notes || "";
        if (values.centre !== initialData.centreCode)
          payload.centre_code = values.centre;

        if (isCheck) {
          if (values.checkNo !== initialData.chequeNumber)
            payload.cheque_number = values.checkNo;
          if (values.checkDate !== initialData.chequeDate)
            payload.cheque_date = values.checkDate;
          if (values.bankName !== initialData.bankName)
            payload.bank_name = values.bankName;
          if (values.branch !== initialData.branch)
            payload.branch = values.branch;
          if (values.micr !== initialData.micrIfsc)
            payload.micr_ifsc = values.micr;
          if (values.payeeName !== initialData.payeeName)
            payload.payee_name = values.payeeName;
        }
        break;
      case "add":
      default:
        // Add mode: full payload
        payload = {
          consumer_account: consumerAccountNo,
          consumer_id: values.accountNo || selectedConsumer?.id,
          invoice_number: values.invoiceNo,
          remote_bill_id: null,
          slip_number: values.slipNo,
          mode: values.mode,
          amount: values.payment,
          payment_date: values.paymentDate,
          notes: values.notes || "",
          centre_code: values.centre,
          ...(isCheck
            ? {
                cheque_number: values.checkNo,
                cheque_date: values.checkDate,
                bank_name: values.bankName,
                branch: values.branch,
                micr_ifsc: values.micr,
                payee_name: values.payeeName,
              }
            : {}),
        };
        break;
    }

    const handleSuccess = () => {
      onSubmit(values, isAddAnother);
      if (!isAddAnother) {
        onClose();
      } else {
        form.reset({
          slipNo: `SLP-${Math.floor(100000 + Math.random() * 900000)}`,
          centre: values.centre || "DTW",
          paymentDate: "",
          mode: "",
        });
        setSelectedConsumer(null);
      }
    };

    if (initialData) {
      updateEntryMutation.mutate(
        { entryId: initialData.id, payload },
        { onSuccess: handleSuccess },
      );
    } else {
      addEntryMutation.mutate(payload, { onSuccess: handleSuccess });
    }
  };

  const errors = form.formState.errors;
  const errorMessages = Object.values(errors)
    .map((e: any) => e?.message)
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] flex flex-col p-0 max-h-[90vh]">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            {initialData ? "Edit Slip Entry" : "Add Slip Entry"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-1">
          <div className="mt-2">
            <DynamicForm
              form={form}
              fields={fields}
              onSubmit={(data) => handleFormSubmit(data, false)}
              onReset={onClose}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
              loading={
                addEntryMutation.isPending || updateEntryMutation.isPending
              }
              config={{
                layout: "vertical",
                showSubmitButton: true,
                submitButtonText: initialData ? "Save Changes" : "Add Entry",
                icon: Check,
                showResetButton: true,
                resetButtonText: "Cancel",
                classes: BUTTON_FIELD_CLASSES,
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
